import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { apiGet, apiPut } from '../utils/api';

interface AppointmentItem {
  id?: number | string;
  appointment_id?: number | string;
  patient_name?: string;
  patient_id?: number;
  pid?: number | string;
  doctor_id?: number;
  doctorId?: number;
  phone?: string;
  mobile?: string;
  phone_number?: string;
  patient_phone?: string;
  contact_number?: string;
  contact_phone?: string;
  patient_mobile?: string;
  [key: string]: any;
}

/** Next whole rupee upward (e.g. 157.50 → 158). */
function roundUpToWholeRupee(amount: number): number {
  const n = Number(amount) || 0;
  return Math.ceil(Math.round(n * 100) / 100);
}

function getAppointmentPhone(apt: AppointmentItem): string {
  const raw =
    apt.phone ??
    apt.mobile ??
    apt.phone_number ??
    apt.patient_phone ??
    apt.contact_number ??
    apt.contact_phone ??
    apt.patient_mobile ??
    '';
  return raw != null ? String(raw).trim() : '';
}

interface BillingSalesInfoProps {
  grandTotal?: number;
  onDataChange?: (data: {
    customerName: string;
    phoneNumber: string;
    paymentMode: string;
    amountPaid: number;
    patientId?: number;
  }) => void;
}

const BillingSalesInfo: React.FC<BillingSalesInfoProps> = ({ grandTotal = 0, onDataChange }) => {
  const [customerName, setCustomerName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [amountPaid, setAmountPaid] = useState<number>(0);
  const [patientId, setPatientId] = useState<number | undefined>(undefined);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [appointments, setAppointments] = useState<AppointmentItem[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownPortalRef = useRef<HTMLDivElement>(null);
  const customerInputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const [appointmentDate, setAppointmentDate] = useState<string>(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [appointmentDoctorId, setAppointmentDoctorId] = useState<string | null>(null);
  const [allDoctorIds, setAllDoctorIds] = useState<string[]>([]);
  const [doctorMappings, setDoctorMappings] = useState<Array<{ doctor_id: number; name?: string }>>([]);
  const [isPolyclinic, setIsPolyclinic] = useState(false);
  const [dlNo, setDlNo] = useState<string | null>(null);
  const [isEditingDlNo, setIsEditingDlNo] = useState(false);
  const [dlNoEditValue, setDlNoEditValue] = useState('');
  const [dlNoSaving, setDlNoSaving] = useState(false);
  const [dlNoError, setDlNoError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('dl_no');
      if (stored) setDlNo(stored);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      const pharmaDetailsStr = localStorage.getItem('pharmaDetails');
      if (pharmaDetailsStr) {
        const pharmaDetails = JSON.parse(pharmaDetailsStr);
        if (pharmaDetails?.mappings && Array.isArray(pharmaDetails.mappings) && pharmaDetails.mappings.length > 0) {
          const mappings = pharmaDetails.mappings;
          // Store doctor mappings for displaying doctor names
          setDoctorMappings(mappings);
          
          // Extract all doctor IDs
          const doctorIds = mappings
            .map((m: any) => m.doctor_id)
            .filter((id: any) => id !== undefined && id !== null)
            .map((id: any) => String(id));
          
          setAllDoctorIds(doctorIds);
          
          // Check if it's a polyclinic (multiple doctor mappings)
          setIsPolyclinic(doctorIds.length > 1);
          
          // Set the first doctor ID as default (for backward compatibility)
          if (doctorIds.length > 0) {
            setAppointmentDoctorId(doctorIds[0]);
          }
        }
      }
    } catch {
      // ignore
    }
  }, []);

  const startEditDlNo = () => {
    setDlNoEditValue(dlNo || '');
    setDlNoError(null);
    setIsEditingDlNo(true);
  };

  const cancelEditDlNo = () => {
    setIsEditingDlNo(false);
    setDlNoEditValue('');
    setDlNoError(null);
  };

  const saveDlNo = async () => {
    const value = (dlNoEditValue || '').trim();
    setDlNoSaving(true);
    setDlNoError(null);
    try {
      const res = await apiPut('auth/dlno', { dlno: value });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Failed to update DL no');
      }
      setDlNo(value || null);
      if (value) localStorage.setItem('dl_no', value);
      else localStorage.removeItem('dl_no');
      setIsEditingDlNo(false);
      setDlNoEditValue('');
    } catch (e: any) {
      setDlNoError(e?.message || 'Failed to update DL no');
    } finally {
      setDlNoSaving(false);
    }
  };

  const fetchAppointments = useCallback(async () => {
    // For polyclinic, we need at least one doctor ID
    // For single doctor, we need appointmentDoctorId
    if (isPolyclinic && allDoctorIds.length === 0) {
      setAppointments([]);
      return;
    }
    if (!isPolyclinic && !appointmentDoctorId) {
      setAppointments([]);
      return;
    }
    
    setAppointmentsLoading(true);
    try {
      const date = appointmentDate || new Date().toISOString().slice(0, 10);
      let allAppointments: AppointmentItem[] = [];
      
      // If polyclinic, fetch appointments for all doctor IDs
      const doctorIdsToFetch = isPolyclinic ? allDoctorIds : [appointmentDoctorId!];
      
      // Fetch appointments for each doctor ID
      const fetchPromises = doctorIdsToFetch.map(async (doctorId) => {
        const endpoint = `appointments/by-date?appointment_date=${encodeURIComponent(date)}&doctor_id=${encodeURIComponent(doctorId)}`;
        
        try {
          const res = await apiGet(endpoint);
          
          if (!res.ok) {
            return [];
          }
          
          const data = await res.json();
          
          // Handle different response structures
          let list: any[] | null = null;
          if (Array.isArray(data)) {
            list = data;
          } else if (data && typeof data === 'object') {
            // Try common response property names
            const possibleList = data.appointments || data.data || data.results || data.items || data.list;
            if (Array.isArray(possibleList)) {
              list = possibleList;
            } else if (!list && Object.keys(data).length > 0) {
              // Check if any property is an array
              const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]));
              if (arrayKey) {
                list = data[arrayKey];
              }
            }
          }
          
          const appointments = Array.isArray(list) ? list : [];
          
          // Ensure each appointment has doctor_id for polyclinic mode
          if (isPolyclinic && appointments.length > 0) {
            return appointments.map((apt: any) => ({
              ...apt,
              doctor_id: apt.doctor_id ?? apt.doctorId ?? Number(doctorId),
              doctorId: apt.doctorId ?? apt.doctor_id ?? Number(doctorId)
            }));
          }
          
          return appointments;
        } catch (error) {
          return [];
        }
      });
      
      // Wait for all requests to complete
      const results = await Promise.all(fetchPromises);
      
      // Combine all appointments from all doctors
      allAppointments = results.flat();
      
      // Remove duplicates based on appointment_id or id
      const uniqueAppointments = allAppointments.reduce((acc: AppointmentItem[], current) => {
        const id = current.appointment_id ?? current.id;
        if (!acc.find(item => (item.appointment_id ?? item.id) === id)) {
          acc.push(current);
        }
        return acc;
      }, []);
      
      setAppointments(uniqueAppointments);
    } catch (error) {
      setAppointments([]);
    } finally {
      setAppointmentsLoading(false);
    }
  }, [appointmentDate, appointmentDoctorId, isPolyclinic, allDoctorIds]);

  const updateDropdownPosition = useCallback(() => {
    if (customerInputRef.current) {
      const rect = customerInputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width
      });
    }
  }, []);

  // Removed automatic fetching - appointments are now fetched only when customer field is clicked

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inWrapper = customerDropdownRef.current?.contains(target);
      const inDropdown = dropdownPortalRef.current?.contains(target);
      if (!inWrapper && !inDropdown) setCustomerDropdownOpen(false);
    };
    if (customerDropdownOpen) {
      updateDropdownPosition(); // run immediately so position is set
      // Removed fetchAppointments from here - now only called when customer field is clicked
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [customerDropdownOpen, updateDropdownPosition]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Notify parent component when data changes
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        customerName,
        phoneNumber,
        paymentMode,
        amountPaid,
        patientId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName, phoneNumber, paymentMode, amountPaid, patientId]);

  // Keep amount paid in sync with bill total (full precision from parent). Do not use Math.round on the
  // value passed in there: different discounts can yield the same rounded rupee while the payable total changed.
  useEffect(() => {
    const raw = Number(grandTotal) || 0;
    setAmountPaid(raw > 0 ? roundUpToWholeRupee(raw) : 0);
  }, [grandTotal]);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    padding: isMobile ? '0.625rem 0.75rem' : '0.75rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: isMobile ? '0.875rem' : '0.9375rem',
    background: 'white',
    color: '#111827',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box'
  };

  const containerStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '100%',
    background: 'white',
    borderRadius: '0',
    padding: isMobile ? '1.25rem 1rem' : '2rem 1.5rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(239, 68, 68, 0.1)',
    marginTop: '1rem',
    boxSizing: 'border-box',
    overflow: 'hidden'
  };

  return (
    <div style={containerStyle}>
      <style>{`
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none !important;
          margin: 0 !important;
          display: none !important;
        }
        input[type="number"] {
          -moz-appearance: textfield !important;
        }
      `}</style>
      {/* Heading row: title + date for appointments */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h2 style={{
            fontSize: isMobile ? '1.125rem' : '1.5rem',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0,
            letterSpacing: '-0.02em'
          }}>
            Sales Information
          </h2>
          <p style={{
            fontSize: isMobile ? '0.8125rem' : '0.875rem',
            color: '#6b7280',
            margin: '0.25rem 0 0 0',
            fontWeight: '500'
          }}>
            Customer and payment details
          </p>
        </div>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexShrink: 0
        }}>
          <span style={{
            fontSize: isMobile ? '0.8125rem' : '0.875rem',
            fontWeight: '600',
            color: '#374151',
            whiteSpace: 'nowrap'
          }}>
            Date
          </span>
          <input
            type="date"
            value={appointmentDate}
            onChange={(e) => setAppointmentDate(e.target.value)}
            style={{
              ...inputStyle,
              width: 'auto',
              minWidth: isMobile ? '140px' : '160px'
            }}
          />
        </label>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexShrink: 0,
          fontSize: isMobile ? '0.8125rem' : '0.875rem',
          color: '#374151',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: '600', whiteSpace: 'nowrap' }}>DL NO:</span>
          {isEditingDlNo ? (
            <>
              <input
                type="text"
                value={dlNoEditValue}
                onChange={(e) => setDlNoEditValue(e.target.value)}
                placeholder="Enter DL no"
                style={{
                  ...inputStyle,
                  width: isMobile ? '120px' : '160px',
                  padding: '0.35rem 0.5rem',
                  fontSize: '0.8125rem'
                }}
                autoFocus
              />
              <button
                type="button"
                onClick={saveDlNo}
                disabled={dlNoSaving}
                style={{
                  padding: '0.35rem 0.6rem',
                  background: dlNoSaving ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: dlNoSaving ? 'not-allowed' : 'pointer'
                }}
              >
                {dlNoSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={cancelEditDlNo}
                disabled={dlNoSaving}
                style={{
                  padding: '0.35rem 0.6rem',
                  background: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  cursor: dlNoSaving ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <span style={{ whiteSpace: 'nowrap' }}>{dlNo || '–'}</span>
              <button
                type="button"
                onClick={startEditDlNo}
                title="Edit DL no"
                style={{
                  padding: '0.2rem',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </>
          )}
        </div>
        {dlNoError && (
          <div style={{
            flexBasis: '100%',
            fontSize: '0.75rem',
            color: '#b91c1c',
            marginTop: '0.25rem'
          }}>
            {dlNoError}
          </div>
        )}
      </div>

      {/* Form Fields */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: isMobile ? '1rem' : '1.25rem',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Customer Name with appointments dropdown */}
        <div
          ref={customerDropdownRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            position: 'relative'
          }}
        >
          <label style={{
            fontSize: isMobile ? '0.8125rem' : '0.875rem',
            fontWeight: '600',
            color: '#374151'
          }}>
            Customer Name
          </label>
          <input
            ref={customerInputRef}
            type="text"
            value={customerName}
            onChange={(e) => {
              setCustomerName(e.target.value);
              // Clear patientId when manually typing (not selecting from dropdown)
              setPatientId(undefined);
            }}
            placeholder="Enter or select customer name"
            style={inputStyle}
            onFocus={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.borderColor = '#ef4444';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
              }
              if (!customerDropdownOpen) {
                setCustomerDropdownOpen(true);
                // Fetch appointments only when customer field is clicked/focused (not already open)
                fetchAppointments();
              }
            }}
            onClick={() => {
              if (!customerDropdownOpen) {
                setCustomerDropdownOpen(true);
                // Fetch appointments only when customer field is clicked (not already open)
                fetchAppointments();
              }
            }}
            onBlur={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          />
          {customerDropdownOpen && dropdownPosition.width > 0 &&
            createPortal(
              <div
                ref={dropdownPortalRef}
                style={{
                  position: 'fixed',
                  top: dropdownPosition.top + 2,
                  left: dropdownPosition.left,
                  width: dropdownPosition.width,
                  maxHeight: '220px',
                  overflow: 'auto',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 10002
                }}
              >
                {appointmentsLoading ? (
                  <div style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    Loading…
                  </div>
                ) : appointments.length === 0 ? (
                  <div style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    No appointments for today
                  </div>
                ) : (
                  appointments.map((apt, idx) => {
                    // Try multiple possible field names for patient name
                    const name = apt.patient_name ?? 
                                 apt.name ?? 
                                 apt.patientName ?? 
                                 apt.patient?.name ?? 
                                 apt.patient?.patient_name ?? 
                                 '';
                    const phone = getAppointmentPhone(apt);
                    
                    // Get doctor name if polyclinic
                    const aptDoctorId = apt.doctor_id ?? apt.doctorId;
                    const doctorName = isPolyclinic && aptDoctorId 
                      ? doctorMappings.find(m => m.doctor_id === Number(aptDoctorId))?.name 
                      : null;
                    
                    return (
                      <div
                        key={apt.id ?? apt.appointment_id ?? idx}
                        role="button"
                        tabIndex={0}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setCustomerName(name);
                          setPhoneNumber(phone);
                          // Use PID if available, otherwise fall back to patient_id
                          const pidValue = apt.pid !== undefined ? apt.pid : apt.patient_id;
                          setPatientId(typeof pidValue === 'string' ? parseInt(pidValue, 10) : pidValue);
                          setCustomerDropdownOpen(false);
                        }}
                        style={{
                          padding: '0.6rem 1rem',
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          borderBottom: idx < appointments.length - 1 ? '1px solid #f3f4f6' : 'none'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f3f4f6';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <div style={{ fontWeight: 500, color: '#111827' }}>
                            {name || '–'}
                          </div>
                          {isPolyclinic && doctorName && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              Dr. {doctorName}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>,
              document.body
            )}
        </div>

        {/* Phone Number */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0
        }}>
          <label style={{
            fontSize: isMobile ? '0.8125rem' : '0.875rem',
            fontWeight: '600',
            color: '#374151'
          }}>
            Phone Number <span style={{ fontWeight: '400', color: '#9ca3af' }}>(Optional)</span>
          </label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="Enter phone number"
            style={inputStyle}
            onFocus={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.borderColor = '#ef4444';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
              }
            }}
            onBlur={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          />
        </div>

        {/* Payment Mode */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0
        }}>
          <label style={{
            fontSize: isMobile ? '0.8125rem' : '0.875rem',
            fontWeight: '600',
            color: '#374151'
          }}>
            Payment Mode
          </label>
          <select
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value)}
            style={{
              ...inputStyle,
              cursor: 'pointer',
              background: 'white',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              paddingRight: '2.5rem'
            }}
            onFocus={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.borderColor = '#ef4444';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
              }
            }}
            onBlur={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            <option value="">Select payment mode</option>
            <option value="Cash">Cash</option>
            <option value="Card">Card</option>
            <option value="UPI">UPI</option>
            <option value="Net Banking">Net Banking</option>
          </select>
        </div>

        {/* Amount Paid */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0
        }}>
          <label style={{
            fontSize: isMobile ? '0.8125rem' : '0.875rem',
            fontWeight: '600',
            color: '#374151'
          }}>
            Amount Paid
          </label>
          <input
            type="number"
            value={amountPaid || ''}
            onChange={(e) => {
              const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
              setAmountPaid(isNaN(value) ? 0 : value);
            }}
            placeholder="Enter amount paid"
            step="0.01"
            min="0"
            style={{
              ...inputStyle,
              MozAppearance: 'textfield',
              WebkitAppearance: 'none',
              appearance: 'none'
            }}
            onFocus={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.borderColor = '#ef4444';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
              }
            }}
            onBlur={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default BillingSalesInfo;

